import { globalConfirm } from '../components/ConfirmDialog';
import { useFormatter } from '../utils/formatters';
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/layout/AppShell';
import { PageHeader, DataTableToolbar, PaginationBar, ActiveBadge, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { SortableTableHeader, useSortState } from '../components/SortableTableHeader';
import TypeAheadLookup from '../components/TypeAheadLookup';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api, { getApiErrorMessage } from '../services/api';
import ModuleReports from './Reports/ModuleReports';
import { CATEGORIES_REPORTS } from './Reports/reportConfigs';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { Plus, Download, Trash2, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DataFilesAnalytics from './DataFilesAnalytics/DataFilesAnalytics';
import { categoriesConfig } from './DataFilesAnalytics/configs';
import { LayoutDashboard as AnalyticsIcon } from 'lucide-react';

const EMPTY = { code: '', description: '', budget: 0, parent_code: '', active: true, linked_suppliers: [], inventory_valuation_method: '' };

export default function CategoryCodesPage() {
  const [records, setRecords] = useState([]);
  const { canWrite: _canWrite } = usePermissions();
  const canWriteData = _canWrite('category_code');
  const { systemSettings } = useAuth();
  const hasInventoryModule = [...(systemSettings?.modules || []), ...(systemSettings?.enabled_modules || [])].includes('inventory');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [viewTab, setViewTab] = useState('analytics');
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ supplier_code: '', supplier_name: '' });

  const [sortState, handleSort] = useSortState('code', 'asc');
  const { systemCurrency } = useFormatter();
  const [impactData, setImpactData] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/data/categories', { params: { search, page, page_size: pageSize, sort_by: sortState.field, sort_dir: sortState.dir } });
      const data = res.data.data || [];
      const starred = data.filter(r => r.is_favorite);
      const unstarred = data.filter(r => !r.is_favorite);
      setRecords([...starred, ...unstarred]);
      setTotal(res.data.total);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load categories'); }
    setLoading(false);
  }, [search, page, pageSize, sortState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setIsEdit(false); setImpactData(null); setModalOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setIsEdit(true); setImpactData(null); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/api/data/categories/${form.code}`, form); toast.success('Category updated'); }
      else { await api.post('/api/data/categories', form); toast.success('Category created'); }
      setModalOpen(false); load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const deleteRecord = async () => {
    if (!await globalConfirm('Delete this category?')) return;
    try { await api.delete(`/api/data/categories/${form.code}`); toast.success('Deleted'); setModalOpen(false); load(); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Delete failed')); }
  };

  const toggleFav = async (r) => {
    const { toggleFavorite } = await import('../utils/favorites');
    const result = await toggleFavorite('categories', r.code);
    if (result !== null) {
      setRecords(prev => {
        const updated = prev.map(rec => rec.code === r.code ? { ...rec, is_favorite: result } : rec);
        const starred = updated.filter(rec => rec.is_favorite);
        const unstarred = updated.filter(rec => !rec.is_favorite);
        return [...starred, ...unstarred];
      });
    }
  };

  const addSupplier = () => {
    if (!newSupplier.supplier_code) return;
    setForm(prev => ({ ...prev, linked_suppliers: [...(prev.linked_suppliers || []), { ...newSupplier }] }));
    setNewSupplier({ supplier_code: '', supplier_name: '' });
  };

  const removeSupplier = (idx) => setForm(prev => ({ ...prev, linked_suppliers: prev.linked_suppliers.filter((_, i) => i !== idx) }));

  return (
    <AppShell>
      <PageHeader
        title="Category Code"
        description={`Total records: ${total}`}
        helpGuide="data-files"
        actions={viewTab === 'list' ? <>
          <Button size="sm" variant="outline" onClick={() => {}} data-testid="category-export-button"><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}} data-testid="category-add-button"><Plus className="h-4 w-4 mr-1" /> Add New</Button>
        </> : null}
      />
      <div className="flex gap-4 border-b mb-4" data-testid="categories-view-tabs">
        <button onClick={() => setViewTab('analytics')} data-testid="categories-tab-analytics"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'analytics' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <AnalyticsIcon className="h-4 w-4 inline mr-1.5" /> Analytics
        </button>
        <button onClick={() => setViewTab('list')} data-testid="categories-tab-list"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'list' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <FileTextIcon className="h-4 w-4 inline mr-1.5" /> Categories
        </button>
        <button onClick={() => setViewTab('reports')} data-testid="categories-tab-reports"
          className={`pb-2 text-sm font-medium transition-colors ${viewTab === 'reports' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="h-4 w-4 inline mr-1.5" /> Reports
        </button>
      </div>
      {viewTab === 'analytics' ? (<DataFilesAnalytics {...categoriesConfig} onCardClick={() => setViewTab('list')} />) : viewTab === 'reports' ? (
        <ModuleReports reports={CATEGORIES_REPORTS} apiPrefix="/api/module-reports/data" module="Categories" />
      ) : (<>
      <DataTableToolbar
        searchValue={search} onSearch={v => { setSearch(v); setPage(1); }} onClear={() => { setSearch(''); setPage(1); }}
        searchPlaceholder="Search category codes..."
      />

      <div className="rounded-xl border bg-card">
        {loading ? <div className="py-12"><LoadingSpinner /></div> : records.length === 0 ? <EmptyState title="No categories found" action={<Button size="sm" onClick={openCreate} style={canWriteData ? {} : {display:"none"}}>Add Category</Button>} /> : (
          <Table className="data-table" aria-label="Category codes table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <SortableTableHeader label="Category Code" field="code" sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label="Description" field="description" sortState={sortState} onSort={handleSort} />
                <SortableTableHeader label={`Budget (${systemCurrency})`} field="budget" sortState={sortState} onSort={handleSort} className="text-right" />
                <SortableTableHeader label="Active" field="active" sortState={sortState} onSort={handleSort} />
                {hasInventoryModule && <SortableTableHeader label="Valuation Method" field="inventory_valuation_method" sortState={sortState} onSort={handleSort} />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.code} className="cursor-pointer" onClick={() => openEdit(r)}>
                  <TableCell onClick={e => e.stopPropagation()}>
                  </TableCell>
                  <TableCell className="font-mono font-medium text-primary">{r.code}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell className="text-right [font-variant-numeric:tabular-nums]">{r.budget ? new Intl.NumberFormat('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}).format(r.budget) : '0.00'}</TableCell>
                  <TableCell><ActiveBadge active={r.active} /></TableCell>
                  {hasInventoryModule && (
                    <TableCell className="text-xs">
                      {r.inventory_valuation_method ? r.inventory_valuation_method.toUpperCase() : <span className="text-muted-foreground">System Default</span>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="px-4">
          <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        </div>
      </div>
      </>)}

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              {isEdit ? 'Edit Category Code' : 'Add Category Code'}
              <div className="flex items-center gap-2 mr-6">
                <span className="text-sm font-normal text-muted-foreground">Active</span>
                <Switch checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} aria-label="Active" />
              </div>
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="suppliers">Linked Suppliers ({(form.linked_suppliers || []).length})</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="cat-code">Category Code *</Label>
                  <Input id="cat-code" value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} placeholder="e.g. IT-HW" disabled={isEdit} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cat-desc">Description *</Label>
                  <Input id="cat-desc" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Category description" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cat-budget">Budget</Label>
                  <Input id="cat-budget" type="number" value={form.budget} onChange={e => setForm(p => ({ ...p, budget: parseFloat(e.target.value) || 0 }))} placeholder="0.00" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cat-parent">Parent Category Code</Label>
                  <TypeAheadLookup
                    endpoint="/api/data/categories"
                    valueField="code"
                    labelField="description"
                    value={form.parent_code || ''}
                    displayValue={form.parent_code || ''}
                    placeholder="Search parent category..."
                    onChange={(code) => setForm(p => ({ ...p, parent_code: code }))}
                  />
                </div>
              </div>
              {hasInventoryModule && (
                <div className="pt-2 border-t mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="cat-valuation-method">Inventory Valuation Method</Label>
                      <select
                        id="cat-valuation-method"
                        value={form.inventory_valuation_method || ''}
                        onChange={e => {
                          const newMethod = e.target.value;
                          setForm(p => ({ ...p, inventory_valuation_method: newMethod }));
                          if (isEdit && form.code && newMethod) {
                            setImpactLoading(true);
                            setImpactData(null);
                            api.get(`/api/inventory/mgmt-reports/valuation-impact?category_code=${encodeURIComponent(form.code)}&new_method=${newMethod}`)
                              .then(r => setImpactData(r.data))
                              .catch(() => setImpactData(null))
                              .finally(() => setImpactLoading(false));
                          } else {
                            setImpactData(null);
                          }
                        }}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        data-testid="category-valuation-method-select"
                      >
                        <option value="">Use System Default</option>
                        <option value="fifo">FIFO (First In, First Out)</option>
                        <option value="lifo">LIFO (Last In, First Out)</option>
                        <option value="wac">WAC (Weighted Average Cost)</option>
                      </select>
                      <p className="text-[11px] text-muted-foreground">Override the system default valuation method for items in this category. Leave blank to use the system default.</p>
                    </div>
                    <div className="flex items-end">
                      {impactLoading && <p className="text-xs text-muted-foreground animate-pulse pb-2">Calculating impact...</p>}
                    </div>
                  </div>
                  {impactData && (
                    <div className={`mt-3 rounded-lg border p-3 text-xs ${impactData.delta > 0 ? 'bg-amber-50 border-amber-200' : impactData.delta < 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`} data-testid="valuation-impact-panel">
                      <div className="font-semibold mb-1.5 flex items-center gap-1.5">
                        <ArrowRightLeft className="h-3.5 w-3.5" />
                        Valuation Change Impact Preview
                      </div>
                      <div className="grid grid-cols-3 gap-3 mb-2">
                        <div>
                          <span className="text-muted-foreground">Current Value</span>
                          <div className="font-bold text-sm">${(impactData.current_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <span className="text-[10px] text-muted-foreground">{impactData.current_method}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Projected Value</span>
                          <div className="font-bold text-sm">${(impactData.projected_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <span className="text-[10px] text-muted-foreground">{impactData.new_method}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Difference</span>
                          <div className={`font-bold text-sm ${impactData.delta > 0 ? 'text-amber-700' : impactData.delta < 0 ? 'text-blue-700' : 'text-green-700'}`}>
                            {impactData.delta >= 0 ? '+' : ''}${(impactData.delta || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className="text-[10px] ml-1">({impactData.delta >= 0 ? '+' : ''}{impactData.delta_pct}%)</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground">{impactData.affected_items} of {impactData.total_items} items affected</span>
                        </div>
                      </div>
                      {(impactData.items || []).length > 0 && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[10px] font-medium hover:underline">Show affected items</summary>
                          <div className="mt-1.5 max-h-32 overflow-auto border rounded bg-background">
                            <table className="w-full text-[10px]">
                              <thead><tr className="bg-muted/40 border-b">
                                <th className="text-left px-2 py-1 font-semibold">Item</th>
                                <th className="text-left px-2 py-1 font-semibold">Location</th>
                                <th className="text-right px-2 py-1 font-semibold">Current</th>
                                <th className="text-right px-2 py-1 font-semibold">Projected</th>
                                <th className="text-right px-2 py-1 font-semibold">Delta</th>
                              </tr></thead>
                              <tbody>
                                {impactData.items.map((item, i) => (
                                  <tr key={i} className="border-b hover:bg-accent/20">
                                    <td className="px-2 py-1 font-mono">{item.item_code}</td>
                                    <td className="px-2 py-1">{item.location_code}</td>
                                    <td className="px-2 py-1 text-right">${item.current_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                    <td className="px-2 py-1 text-right">${item.projected_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                    <td className={`px-2 py-1 text-right font-semibold ${item.delta > 0 ? 'text-amber-700' : item.delta < 0 ? 'text-blue-700' : ''}`}>
                                      {item.delta >= 0 ? '+' : ''}${item.delta.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="suppliers" className="pt-4">
              <div className="flex items-end gap-2 mb-3">
                <div className="flex-1">
                  <Label className="text-xs font-semibold mb-1 block">Add Supplier</Label>
                  <TypeAheadLookup
                    value={newSupplier.supplier_code}
                    displayValue={newSupplier.supplier_code ? `${newSupplier.supplier_code} — ${newSupplier.supplier_name}` : ''}
                    onChange={(code, record) => setNewSupplier({
                      supplier_code: code,
                      supplier_name: record?.supplier_name || '',
                    })}
                    endpoint="/api/data/suppliers"
                    valueField="supplier_code"
                    labelField="supplier_name"
                    secondaryField="city"
                    placeholder="Search by code or name..."
                    excludeValues={(form.linked_suppliers || []).map(s => s.supplier_code)}
                    data-testid="linked-supplier-lookup"
                  />
                </div>
                <Button size="sm" onClick={addSupplier} disabled={!newSupplier.supplier_code} data-testid="linked-supplier-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              {(form.linked_suppliers || []).length === 0 ? <EmptyState title="No linked suppliers" /> : (
                <Table>
                  <TableHeader><TableRow><TableHead>Supplier Code</TableHead><TableHead>Supplier Name</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {form.linked_suppliers.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-xs">{s.supplier_code}</TableCell>
                        <TableCell className="text-sm">{s.supplier_name}</TableCell>
                        <TableCell><Button size="sm" variant="ghost" onClick={() => removeSupplier(i)} aria-label="Remove supplier"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)} data-testid="record-modal-cancel-button">Cancel</Button>
            {isEdit && <Button variant="destructive" onClick={deleteRecord} data-testid="record-modal-delete-button">Delete</Button>}
            <Button onClick={save} disabled={!canWriteData || saving || !form.code || !form.description} data-testid="record-modal-save-button">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
